/**
 * Бюджет человеко-часов (ПЛ-37): план из назначений людей, освоено по проценту выполнения (по шагам — ПЛ-36),
 * факт из табеля; производительность и прогноз трудоёмкости до завершения; разрез и недели.
 */
import type { ScheduleView } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { api } from '../../lib/api';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { LField } from './LField';

type Group = 'section' | 'workType' | 'contractor' | 'zone';
const GROUPS: Record<Group, string> = {
  section: 'По разделам ИСР',
  workType: 'По видам работ',
  contractor: 'По подрядчикам',
  zone: 'По участкам (захваткам)',
};
interface Line {
  key: string;
  name: string;
  plan: number;
  earned: number;
  actual: number;
  productivity: number | null;
  eac: number | null;
}
interface ManHours {
  total: Line;
  groups: Line[];
  weeks: Array<{ week: string; earned: number; actual: number }>;
  unassigned: number;
  counts: { planned: number; withSteps: number };
  output: Array<{
    id: string;
    code: string;
    name: string;
    workType: string | null;
    unit: string | null;
    done: number;
    planNorm: number;
    actualNorm: number;
    outputPct: number;
  }>;
  outputByType: Array<{
    workType: string;
    unit: string | null;
    done: number;
    planNorm: number;
    actualNorm: number;
    outputPct: number;
  }>;
}
const h = (x: number | null) => (x == null ? '—' : Math.round(x).toLocaleString('ru-RU'));
const k = (x: number | null) => (x == null ? '—' : String(x).replace('.', ','));
const dd = (s: string) => s.slice(0, 10).split('-').reverse().join('.');

export function ManHoursPanel({ view }: { view: ScheduleView }) {
  const [groupBy, setGroupBy] = useState<Group>('section');
  const q = useQuery({
    queryKey: ['manhours', view.schedule.id, groupBy, view.schedule.revision],
    queryFn: () => api<ManHours>(`/schedules/${view.schedule.id}/manhours`, { query: { groupBy } }),
  });
  const m = q.data;
  const max = m ? Math.max(1, ...m.weeks.map((w) => Math.max(w.earned, w.actual))) : 1;
  return (
    <div className="flex flex-col gap-4">
      <Panel title="Бюджет человеко-часов" meta="план, освоено и факт">
        <p className="mb-3 text-[13px] text-ink-2">
          План — трудоёмкость людей, назначенных на работы (по смете и нормам). Освоено — план работы ×
          процент выполнения (по шагам выполнения, если они заданы). Факт — часы табеля по работам графика. По
          неделям освоенное по шагам ложится на неделю закрытия шага, без шагов — на неделю окончания работы
          или дату отчёта.
        </p>
        <LField label="Разрез">
          <Select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as Group)}
            className="max-md:h-11"
          >
            {Object.entries(GROUPS).map(([key, t]) => (
              <option key={key} value={key}>
                {t}
              </option>
            ))}
          </Select>
        </LField>
      </Panel>
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {m && !m.counts.planned && !m.total.actual ? (
        <EmptyState title="Трудоёмкости пока нет">
          Назначьте людей на работы в карточках работ или по позициям сметы — план человеко-часов появится
          здесь.
        </EmptyState>
      ) : null}
      {m && (m.counts.planned || m.total.actual) ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {(
              [
                ['План, чел.-ч', h(m.total.plan)],
                ['Освоено, чел.-ч', h(m.total.earned)],
                ['Факт, чел.-ч', h(m.total.actual)],
                ['Производительность', k(m.total.productivity)],
                ['Прогноз на завершение', h(m.total.eac)],
              ] as const
            ).map(([t, v]) => (
              <div key={t} className="rounded-md border border-line bg-surface px-3 py-2">
                <div className="text-[12px] text-ink-2">{t}</div>
                <div
                  className={clsx(
                    'text-[18px] font-semibold',
                    t === 'Производительность' &&
                      m.total.productivity != null &&
                      m.total.productivity < 1 &&
                      'text-danger',
                  )}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[12px] text-ink-2">
            Работ с людьми: {m.counts.planned}, из них по шагам выполнения: {m.counts.withSteps}.
            {m.unassigned
              ? ` Часы табеля без работы графика: ${h(m.unassigned)} — в производительность не входят.`
              : ''}{' '}
            Производительность меньше 1 — на освоенный объём ушло больше часов, чем по плану.
          </p>
          <Panel title={GROUPS[groupBy]}>
            <ScrollX>
              <table className="w-full min-w-[640px] text-[13px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="py-2 pr-2 pl-3">Группа</th>
                    <th className="py-2 pr-2 text-right">План</th>
                    <th className="py-2 pr-2 text-right">Освоено</th>
                    <th className="py-2 pr-2 text-right">Факт</th>
                    <th className="py-2 pr-2 text-right">Произв.</th>
                    <th className="py-2 pr-3 text-right">Прогноз</th>
                  </tr>
                </thead>
                <tbody>
                  {m.groups.map((g) => (
                    <tr key={g.key || g.name} className="border-t border-line">
                      <td className="py-1.5 pr-2 pl-3">{g.name}</td>
                      <td className="num py-1.5 pr-2 text-right">{h(g.plan)}</td>
                      <td className="num py-1.5 pr-2 text-right">{h(g.earned)}</td>
                      <td className="num py-1.5 pr-2 text-right">{h(g.actual)}</td>
                      <td
                        className={clsx(
                          'num py-1.5 pr-2 text-right',
                          g.productivity != null && g.productivity < 1 && 'text-danger',
                        )}
                      >
                        {k(g.productivity)}
                      </td>
                      <td
                        className={clsx(
                          'num py-1.5 pr-3 text-right',
                          g.eac != null && g.eac > g.plan && 'text-danger',
                        )}
                      >
                        {h(g.eac)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </Panel>
          {m.weeks.length ? (
            <Panel title="По неделям" meta="освоено и факт, чел.-ч">
              <svg
                viewBox={`0 0 ${Math.max(12, m.weeks.length) * 28} 110`}
                className="h-44 w-full"
                preserveAspectRatio="none"
                role="img"
                aria-label="Освоено и факт по неделям"
              >
                {m.weeks.map((w, i) => (
                  <g key={w.week}>
                    <rect
                      x={i * 28 + 3}
                      width={10}
                      y={104 - (w.earned / max) * 100}
                      height={(w.earned / max) * 100}
                      fill="var(--color-ink-2)"
                    >
                      <title>
                        {dd(w.week)}: освоено {h(w.earned)}
                      </title>
                    </rect>
                    <rect
                      x={i * 28 + 14}
                      width={10}
                      y={104 - (w.actual / max) * 100}
                      height={(w.actual / max) * 100}
                      fill="var(--color-danger)"
                    >
                      <title>
                        {dd(w.week)}: факт {h(w.actual)}
                      </title>
                    </rect>
                  </g>
                ))}
              </svg>
              <div className="flex flex-wrap justify-between gap-2 text-[12px] text-ink-2">
                <span>с {dd(m.weeks[0]!.week)}</span>
                <span className="inline-flex items-center gap-3">
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block size-2.5 bg-ink-2" /> освоено
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block size-2.5 bg-danger" /> факт
                  </span>
                </span>
                <span>по {dd(m.weeks.at(-1)!.week)}</span>
              </div>
            </Panel>
          ) : null}
          {m.outputByType.length ? (
            <Panel title="Выработка против нормы" meta="по выполненному объёму и часам табеля">
              <ul className="divide-y divide-line text-[14px]">
                {m.outputByType.map((x) => (
                  <li key={`${x.workType}|${x.unit}`} className="flex flex-wrap justify-between gap-2 py-1.5">
                    <span>
                      {x.workType}
                      <span className="block text-[12px] text-ink-2">
                        выполнено {k(x.done)} {x.unit ?? ''} · норма по плану {k(x.planNorm)} чел.-ч, по факту{' '}
                        {k(x.actualNorm)} чел.-ч на {x.unit ?? 'ед.'}
                      </span>
                    </span>
                    <span
                      className={clsx('num font-medium', x.outputPct < 100 ? 'text-danger' : 'text-success')}
                    >
                      {k(x.outputPct)} %
                    </span>
                  </li>
                ))}
              </ul>
              <details className="mt-2 text-[13px]">
                <summary className="min-h-11 cursor-pointer py-2">По работам ({m.output.length})</summary>
                <ul className="divide-y divide-line">
                  {m.output.map((x) => (
                    <li key={x.id} className="flex flex-wrap justify-between gap-2 py-1">
                      <span>
                        {x.code} {x.name}
                      </span>
                      <span className={clsx('num', x.outputPct < 100 ? 'text-danger' : 'text-ink-2')}>
                        {k(x.actualNorm)} против {k(x.planNorm)} чел.-ч/{x.unit ?? 'ед.'} · {k(x.outputPct)} %
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
              <p className="mt-2 text-[12px] text-ink-2">
                Выработка меньше 100 % — на единицу объёма уходит больше часов, чем по норме. Уточнённая норма
                по факту — основа для следующих смет.
              </p>
            </Panel>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
